<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-pencil</v-icon>
        <span class="text-h5">Tenant Bewerken</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="localTenant">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container>
            <v-row>
              <!-- Basic Information -->
              <v-col cols="12">
                <h3 class="mb-3">
                  <v-icon class="mr-2">mdi-information</v-icon>
                  Basis Informatie
                </h3>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localTenant.name"
                  label="Naam *"
                  required
                  :rules="[rules.required]"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localTenant.slug"
                  label="Weergavenaam *"
                  required
                  :rules="[rules.required, rules.slug]"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localTenant.subdomain"
                  label="Subdomain"
                  :rules="[rules.subdomain]"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>

              <!-- Branding -->
              <v-col cols="12">
                <h3 class="mb-3">
                  <v-icon class="mr-2">mdi-palette</v-icon>
                  Branding
                </h3>
              </v-col>

              <v-col cols="12" md="6">
                <HexColorPicker
                  v-model="localTenant.primary_color"
                  label="Primaire Kleur"
                />
              </v-col>

              <!-- Preview (color only) -->
              <v-col v-if="localTenant.primary_color" cols="12">
                <h3 class="mb-3">
                  <v-icon class="mr-2">mdi-eye</v-icon>
                  Voorbeeld
                </h3>
                <v-card :color="localTenant.primary_color" dark class="pa-4">
                  <div class="d-flex align-center">
                    <div>
                      <div class="text-h6">
                        {{ localTenant.display_name || localTenant.name }}
                      </div>
                      <div class="text-body-2 opacity-80">
                        {{ localTenant.subdomain }}.domain.com
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="saving" @click="closeDialog"> Annuleren </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!valid"
          @click="saveTenant"
        >
          Opslaan
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useTenantsStoreRefactor } from "@/stores/tenantsStoreRefactor";
import HexColorPicker from "@/components/ui/HexColorPicker.vue";

export default {
  name: "TenantEditDialog",
  components: { HexColorPicker },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    tenant: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "saved"],
  setup() {
    const tenantsStore = useTenantsStoreRefactor();
    return { tenantsStore };
  },
  data() {
    return {
      valid: false,
      saving: false,
      localTenant: null,
      rules: {
        required: (value) => !!value || "Dit veld is verplicht",
        // Slug: max 10 chars, letters only (A–Z, a–z), no spaces
        slug: (value) => {
          if (!value) return true;
          if (value.length > 10) return "Maximaal 10 tekens";
          return (
            /^[A-Za-z]+$/.test(value) ||
            "Alleen letters toegestaan (A-Z, a-z), geen spaties"
          );
        },
        color: (value) =>
          !value ||
          /^#[0-9A-Fa-f]{6}$/.test(value) ||
          "Moet een geldige hex kleur zijn (bijv. #1976d2)",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    tenant: {
      handler(newTenant) {
        if (newTenant) {
          this.localTenant = { ...newTenant };
        }
      },
      immediate: true,
    },
    dialog(newValue) {
      if (newValue && this.tenant) {
        this.localTenant = { ...this.tenant };
      }
    },
  },
  methods: {
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.localTenant = null;
      this.valid = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async saveTenant() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.saving = true;
      try {
        const updatedTenant = await this.tenantsStore.update({
          id: this.localTenant.id,
          name: this.localTenant.name,
          subdomain: this.localTenant.subdomain,
          primary_color: this.localTenant.primary_color,
          description: this.localTenant.description,
          slug: this.localTenant.slug,
        });

        this.$emit("saved", updatedTenant);
        this.closeDialog();
      } catch (error) {
        console.error("Error saving tenant:", error);
        // TODO: Show error notification
        alert(
          "Er is een fout opgetreden bij het opslaan. Probeer het opnieuw.",
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.v-card-title {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
